import { Button } from 'react-bootstrap'
import { t } from '../locale'
import { JSX } from 'react'

interface CheckHitProps {
  hit: Record<string, unknown>
  onDelete?: () => void
  onRestore?: () => void
}

const INTERNAL_KEYS = new Set(['deleted', 'invalid', 'Status', 'HitType'])

function renderValue(value: unknown, depth = 0): JSX.Element {
  if (value == null) return <span className='text-muted'>—</span>

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className='text-muted'>—</span>
    if (value.every(item => typeof item !== 'object' || item === null)) {
      return <span>{value.map(String).join(', ')}</span>
    }
    return (
      <div className={depth > 0 ? 'ps-3 mt-1' : 'mt-1'}>
        {value.map((item, i) => (
          <div key={i} className='mb-1'>
            {renderValue(item, depth + 1)}
          </div>
        ))}
      </div>
    )
  }

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>
    const entries = Object.entries(obj).filter(([, v]) => v != null && v !== '' && v !== false)
    if (entries.length === 0) return <span className='text-muted'>—</span>
    return (
      <div className={depth > 0 ? 'ps-3' : ''}>
        {entries.map(([k, v]) => (
          <div key={k}>
            <b>{k}</b>: {renderValue(v, depth + 1)}
          </div>
        ))}
      </div>
    )
  }

  return <span>{String(value)}</span>
}

function renderFieldContent(key: string, value: unknown): JSX.Element {
  return (
    <>
      <b>{key}</b>: {renderValue(value)}
    </>
  )
}

export default function CheckHit({ hit, onDelete, onRestore }: CheckHitProps) {
  // Eintrag-based (KYC)
  if (hit.Eintrag && typeof hit.Eintrag === 'object') {
    const eintrag = hit.Eintrag as Record<string, unknown>
    return (
      <div className='pb-3 mb-3 border-bottom'>
        {hit.Liste != null && hit.Liste !== '' && (
          <div>
            <b>Liste</b>: {String(hit.Liste)}
          </div>
        )}
        {Object.entries(eintrag)
          .filter(([k, v]) => !INTERNAL_KEYS.has(k) && v != null && v !== '' && v !== false)
          .map(([k, v]) => (
            <div key={k}>{renderFieldContent(k, v)}</div>
          ))}
        {(onDelete || onRestore) && (
          <div className='mt-2'>
            {onRestore ? (
              <Button variant='outline-success' size='sm' onClick={onRestore}>
                {t('update')}
              </Button>
            ) : (
              <Button variant='outline-danger' size='sm' onClick={onDelete}>
                {t('delete')}
              </Button>
            )}
          </div>
        )}
      </div>
    )
  }

  // data-based (AML)
  if (hit.data && typeof hit.data === 'object') {
    const data = hit.data as Record<string, unknown>
    const attrs = (
      data.attributes && typeof data.attributes === 'object' ? data.attributes : {}
    ) as Record<string, unknown>
    const rawDs = (hit.datasets ?? attrs.datasets) as unknown
    const datasets = Array.isArray(rawDs)
      ? rawDs.map(String)
      : typeof rawDs === 'string' && rawDs
        ? [rawDs]
        : []
    return (
      <div className='pb-3 mb-3 border-bottom'>
        {datasets.length > 0 && (
          <div>
            <b>Datasets</b>: {datasets.join(', ')}
          </div>
        )}
        {Object.entries(attrs)
          .filter(([k, v]) => k !== 'datasets' && v != null && v !== '' && v !== false)
          .map(([k, v]) => (
            <div key={k}>{renderFieldContent(k, v)}</div>
          ))}
      </div>
    )
  }

  // Generic key-value
  return (
    <div className='pb-3 mb-3 border-bottom'>
      {Object.entries(hit)
        .filter(
          ([k, v]) =>
            !INTERNAL_KEYS.has(k) &&
            v != null &&
            v !== '' &&
            v !== false &&
            String(v).toUpperCase() !== 'UNKNOWN'
        )
        .map(([k, v]) => (
          <div key={k}>{renderFieldContent(k, v)}</div>
        ))}
    </div>
  )
}
